import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { blogService, BlogPost, normalizeImagePath } from '../../services/blogService';
import { NOT_FOUND, useDocumentHead } from '../../seo/documentHead';

const StoryDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [story, setStory] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStory = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        setError(null);
        const data = await blogService.fetchStoryBySlug(slug);
        setStory(data);
      } catch (err) {
        console.error('Error fetching story:', err);
        setError(err instanceof Error ? err.message : 'Failed to load story');
      } finally {
        setLoading(false);
      }
    };

    fetchStory();
  }, [slug]);

  useDocumentHead(
    story
      ? {
          title: `${story.title} | Stamp Out Suicide`,
          description: story.excerpt || undefined,
          canonicalPath: `/stories/${story.slug}`,
          image: normalizeImagePath(story.featuredImage?.path),
        }
      : error
        ? { title: NOT_FOUND.title, noindex: true }
        : null
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error || 'Story not found'}
        </div>
        <Link to="/stories" className="btn btn-outline-primary">
          <i className="fas fa-arrow-left me-2"></i>Back to Stories
        </Link>
      </div>
    );
  }

  const authorName = story.author
    ? `${story.author.firstName} ${story.author.lastName}`
    : 'Anonymous';

  return (
    <article className="container py-5">
      {/* Featured Image */}
      {story.featuredImage && (
        <figure className="mb-4">
          <img
            src={normalizeImagePath(story.featuredImage.path)}
            alt={story.featuredImage.alt || story.title}
            className="img-fluid w-100 rounded"
            style={{ maxHeight: '500px', objectFit: 'cover' }}
          />
          {story.featuredImage.caption && (
            <figcaption className="text-muted text-center mt-2">
              {story.featuredImage.caption}
            </figcaption>
          )}
        </figure>
      )}

      {/* Header */}
      <header className="mb-4">
        <h1 className="display-4 fw-bold mb-3">{story.title}</h1>
        <div className="text-muted">
          <span>By {authorName}</span>
          <span className="mx-2">|</span>
          <span>{formatDate(story.publishedAt || story.createdAt)}</span>
        </div>
      </header>

      {/* Content */}
      <div className="story-content mb-5">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{story.content}</ReactMarkdown>
      </div>

      {/* Footer Actions */}
      <footer className="border-top pt-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <Link to="/stories" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-2"></i>Back to Stories
          </Link>
          <Link to="/submit" className="btn btn-primary">
            <i className="fas fa-edit me-2"></i>Write a Note
          </Link>
        </div>
      </footer>
    </article>
  );
};

export default StoryDetailPage;
